import { Button, Check, Loader2, MapPin, mergeClassName } from '@chain-zen/ui'

import type { IELDAsset } from '../../../../../../../data/queries/eld-apps'
import { getAssetStatus } from '../../../../../../../utils/eld-utils'
import type { TActiveAssetType } from '../../hooks/use-eld-asset-selection'
import { LocationPreviewPanel } from './location-preview-panel'

interface IPreviewState {
  assetNumber: string | null
  hasNoLocation: boolean
  isLoading: boolean
  location: {
    address?: unknown | null
    isoDateTime: string
  } | null
}

interface IAssetItemProps {
  activeAssetType: TActiveAssetType
  asset: IELDAsset
  clearPreview: () => void
  currentValue?: string | null
  hasSelectedELDApp: boolean
  isLoadingUpdate: boolean
  onSelect: (asset: IELDAsset) => void
  previewLocation: (asset: IELDAsset) => void
  previewState: IPreviewState
  selectingAsset: string | null
  selectedItem: IELDAsset | null
}

/**
 * Individual asset card with selection indicator and location preview.
 */
export const AssetItem = ({
  activeAssetType,
  asset,
  clearPreview,
  currentValue,
  hasSelectedELDApp,
  isLoadingUpdate,
  onSelect,
  previewLocation,
  previewState,
  selectingAsset,
  selectedItem
}: IAssetItemProps) => {
  const assetNumber = asset.assetNumber || ''
  if (!assetNumber) return null

  const isCurrentValue = currentValue === assetNumber
  const isSelected = selectedItem?.assetNumber === assetNumber
  const isSelecting = selectingAsset === assetNumber && isLoadingUpdate
  const isPreviewing = previewState.assetNumber === assetNumber && previewState.isLoading
  const isPreviewOpen = previewState.assetNumber === assetNumber && !previewState.isLoading
  const isPreviewActive = previewState.assetNumber === assetNumber

  const assetStatus = isPreviewOpen && previewState.location ? getAssetStatus(previewState.location.isoDateTime) : null
  const hasNoLocation = isPreviewOpen && previewState.hasNoLocation

  const getCardBorderClasses = (): string => {
    if (isSelected) return 'border-emerald-500 bg-emerald-500/5'
    if (isCurrentValue) return 'border-zinc-300 dark:border-zinc-600 bg-muted/50'
    return 'border-border hover:border-zinc-300 dark:hover:border-zinc-700 bg-muted/30'
  }

  const getPreviewButtonClasses = (): string => {
    const base = 'px-2.5 py-1.5 rounded text-xs transition-all flex items-center gap-1.5 shrink-0 cursor-pointer'
    if (isPreviewing) return mergeClassName(base, 'bg-amber-500/20 text-amber-400 border border-amber-500/30')
    if (isPreviewOpen) {
      if (hasNoLocation)
        return mergeClassName(base, 'bg-muted text-muted-foreground border border-zinc-300 dark:border-zinc-600')
      if (assetStatus === 'active')
        return mergeClassName(base, 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30')
      return mergeClassName(base, 'bg-red-500/20 text-red-400 border border-red-500/30')
    }
    return mergeClassName(
      base,
      'bg-muted text-muted-foreground hover:bg-zinc-200 dark:hover:bg-zinc-600 hover:text-foreground'
    )
  }

  const handlePreviewClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    if (isPreviewActive) {
      clearPreview()
      return
    }
    void previewLocation(asset)
  }

  return (
    <Button
      type="button"
      onClick={() => !isSelecting && onSelect(asset)}
      variant="ghost"
      className={mergeClassName(
        'w-full h-auto p-0 text-left rounded-lg border transition-all overflow-hidden shrink-0 flex flex-col items-stretch',
        getCardBorderClasses()
      )}
    >
      {/* Main Row */}
      <div className="py-2.5 px-3 flex items-center gap-2.5">
        {/* Radio-style selection indicator */}
        <div
          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all shrink-0 ${
            isSelected ? 'border-emerald-500 bg-emerald-500' : 'border-zinc-300 dark:border-zinc-600'
          }`}
        >
          {!!isSelected && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
        </div>

        {/* Type icon */}
        <div
          className={`w-7 h-7 rounded flex items-center justify-center shrink-0 ${
            activeAssetType === 'truck' ? 'bg-blue-500/20' : 'bg-amber-500/20'
          }`}
        >
          <span className="text-sm">{activeAssetType === 'truck' ? '🚚' : '🚛'}</span>
        </div>

        {/* Asset info */}
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center gap-2">
            <span className="text-foreground text-sm font-medium truncate">{assetNumber}</span>
            {!!isCurrentValue && (
              <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground shrink-0">
                Current
              </span>
            )}
            {!!assetStatus && (
              <span
                className={mergeClassName(
                  'px-1.5 py-0.5 rounded-full text-xs font-medium shrink-0',
                  assetStatus === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                )}
              >
                {assetStatus === 'active' ? '● Active' : '● Inactive'}
              </span>
            )}
          </div>
        </div>

        {/* Preview button */}
        <div
          role="button"
          tabIndex={0}
          onClick={handlePreviewClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handlePreviewClick(e)
            }
          }}
          className={mergeClassName(
            getPreviewButtonClasses(),
            !hasSelectedELDApp && !isPreviewing && 'opacity-50 cursor-not-allowed'
          )}
        >
          {isPreviewing ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Loading</span>
            </>
          ) : (
            <>
              <MapPin className="w-3 h-3" />
              <span>{isPreviewOpen ? 'Hide' : 'Preview'}</span>
            </>
          )}
        </div>
      </div>

      {/* Location Preview Panel */}
      {!!isPreviewOpen && !!previewState.location && (
        <LocationPreviewPanel address={previewState.location.address} isoDateTime={previewState.location.isoDateTime} />
      )}

      {/* No Location Message */}
      {!!isPreviewOpen && !!previewState.hasNoLocation && (
        <div className="px-3 pb-2.5 pt-0">
          <div className="bg-card rounded-lg p-2.5 border border-border">
            <div className="flex items-center gap-2.5">
              <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-muted-foreground text-sm">No location data available</div>
                <div className="text-muted-foreground text-xs mt-0.5">This asset has no GPS location recorded</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Button>
  )
}
